import { ApiError } from '@/api/types';
import { t } from '@/i18n';
import { sendErrorText } from '@/lib/errors';
import { publicOrigin } from '@/lib/app';
import type { SharedContent } from '@/lib/appShare';
import { CHAT_TTL_SECONDS, CHAT_VIEWS, QUICK_TTL_SECONDS, QUICK_VIEWS, quickSendProblem } from '@/lib/quickSend';

import { createBridge } from './createBridge';

/** A request that got no answer says where it was going and what the browser said, so a notice can be read back to a developer. */
function withDetail(cause: ApiError, text: string): string {
  return cause.code === 'network' && cause.detail !== undefined ? `${text} (${cause.detail})` : text;
}

/** A quick send that cannot go; the message is for the person. */
export class QuickSendError extends Error {}

/**
 * Seals what the shortcut sent, as the Create button would for a password with the default options (one day, one
 * view), and resolves to the link with the key in its fragment. A bridge left without its secret is taken down
 * again by `createBridge`.
 */
export async function sendQuick(content: SharedContent): Promise<string> {
  const problem = quickSendProblem(content);
  if (problem !== null) throw new QuickSendError(problem);
  try {
    const path = await createBridge({
      kind: 'password',
      secretText: content.text ?? '',
      file: content.file,
      ttl: QUICK_TTL_SECONDS,
      views: QUICK_VIEWS,
      onStep: () => undefined,
      onProgress: () => undefined,
      isCancelled: () => false,
      immediate: true,
    });
    return `${publicOrigin()}${path}`;
  } catch (cause) {
    if (cause instanceof ApiError) throw new QuickSendError(withDetail(cause, sendErrorText(cause, t('quickSend.bridgeFailed'))));
    throw new QuickSendError(t('quickSend.bridgeFailed'));
  }
}

/**
 * Opens a private chat as the Secure chat tile does, for a day and two devices, and resolves to its link with the key
 * in the fragment. The chat starts empty: nothing secret passes through here.
 */
export async function createChat(): Promise<string> {
  try {
    const path = await createBridge({
      kind: 'chat',
      secretText: '',
      file: null,
      ttl: CHAT_TTL_SECONDS,
      views: CHAT_VIEWS,
      onStep: () => undefined,
      onProgress: () => undefined,
      isCancelled: () => false,
      immediate: true,
    });
    return `${publicOrigin()}${path}`;
  } catch (cause) {
    if (cause instanceof ApiError) throw new QuickSendError(withDetail(cause, sendErrorText(cause, t('quickSend.chatFailed'))));
    throw new QuickSendError(t('quickSend.chatFailed'));
  }
}
